import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { errorMessage } from '@/lib/errors';

const loginSchema = z.object({
  email: z.string().email({ message: 'Introduce un email válido' }),
  password: z.string().min(6, { message: 'La contraseña debe tener al menos 6 caracteres' }),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  redirectTo?: string | null;
}

export const LoginForm = ({ redirectTo }: LoginFormProps) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        if (errorMessage(error).includes('Invalid login credentials')) {
          toast({
            title: t('login.errorTitle'),
            description: t('login.errorInvalidCredentials'),
            variant: 'destructive',
          });
        } else {
          toast({
            title: t('login.errorTitle'),
            description: errorMessage(error),
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: t('login.successTitle'),
          description: t('login.successDesc'),
        });

        // Redirigir según tipo de usuario o parámetro redirect
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Si hay un redirect específico, usarlo
          if (redirectTo) {
            navigate(redirectTo);
            return;
          }

          const { data: profile } = await supabase
            .from('profiles')
            .select('user_type')
            .eq('id', user.id)
            .maybeSingle();

          if (profile?.user_type === 'provider') {
            navigate('/dashboard');
          } else {
            navigate('/');
          }
        } else {
          navigate(redirectTo || '/');
        }
      }
    } catch (error) {
      toast({
        title: t('login.errorTitle'),
        description: t('login.errorGeneric'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        toast({
          title: t('login.errorTitle'),
          description: errorMessage(error),
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('login.errorTitle'),
        description: t('login.errorGoogle'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="w-full max-w-md relative overflow-hidden rounded-[24px] bg-white shadow-ref-card">
      {/* Gold top line */}
      <div className="h-[3px] w-full bg-gradient-to-r from-[#C49520] to-[#B8860B]" />

      <div className="p-8 space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="font-heading text-2xl text-[#2B2520]">{t('login.welcome')}</h1>
          <p className="font-body text-[#8C7E6F] text-sm">
            {t('login.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="font-body text-sm font-medium text-[#2B2520]">
              {t('login.emailLabel')}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="tu@email.com"
              {...register('email')}
              disabled={loading}
              className="font-body bg-white border-[#E8E4DD] text-[#2B2520] placeholder:text-[#8C7E6F]/50 focus:border-[#B8860B] focus:ring-[#B8860B]/20 rounded-xl h-11"
            />
            {errors.email && (
              <p className="font-body text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-body text-sm font-medium text-[#2B2520]">
              {t('login.passwordLabel')}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              disabled={loading}
              className="font-body bg-white border-[#E8E4DD] text-[#2B2520] placeholder:text-[#8C7E6F]/50 focus:border-[#B8860B] focus:ring-[#B8860B]/20 rounded-xl h-11"
            />
            {errors.password && (
              <p className="font-body text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Link to="/forgot-password" className="font-body text-sm text-[#B8860B] hover:text-[#D4A526] hover:underline">
              {t('login.forgotPassword')}
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full font-body font-semibold text-white rounded-xl h-11 transition-all duration-300"
            style={{ background: 'linear-gradient(135deg, #C49520, #B8860B)' }}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('login.signingIn')}
              </>
            ) : (
              t('login.signIn')
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#E8E4DD]" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 font-body text-[#8C7E6F]">
                {t('login.orContinueWith')}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full font-body bg-white border-[#E8E4DD] text-[#2B2520] hover:bg-[#F9F5EE] hover:border-[#B8860B]/30 rounded-xl h-11"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {t('login.signInGoogle')}
          </Button>
        </form>

        <p className="text-center font-body text-sm text-[#8C7E6F]">
          {t('login.noAccount')}{' '}
          <Link to="/register" className="text-[#B8860B] hover:text-[#D4A526] font-semibold hover:underline">
            {t('login.register')}
          </Link>
        </p>
      </div>
    </div>
  );
};
